import { ActionButton, ActionIconButton, ActionToggleGroup } from '../../../components/ui/ActionButton';
import { AiActionButton } from '../../../components/AiActionButton';
import '../../../styles/workout-builder-flutter.css';
import { workoutTypesForCreation } from '../../../utils/workoutTypeDisplay';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiStructBlock } from '../../../utils/workoutStructure';
import { calculatePlannedMetricsFromBlocks } from '../../../utils/plannedWorkoutMetrics';
import {
  buildFtpPercentOptions,
  buildLthrPercentOptions,
  normalizeWorkoutBlocksTargets,
  type PrimaryIntensityMetric,
} from '../../../utils/intensityMetricHelpers';
import {
  parseWorkoutBuilderSettings,
  type WorkoutBuilderSettings,
} from '../../../utils/workoutBuilderSettings';
import { athleteSettingsApi } from '../../../services/athleteSettingsApi';
import { forms, library, workouts, type WorkoutType } from '../../../services/api';
import { WorkoutBuilder } from './WorkoutBuilder';
import { duplicateBlockAt } from './workoutBuilderReorder';
import { WorkoutStepConfig } from './WorkoutStepConfig';
import type { WorkoutBuilderEditConfig, WorkoutBuilderInitialDraft } from '../../../pages/WorkoutBuilderPage';
import {
  WorkoutAiPromptSheet,
  type WorkoutAiApplyPayload,
} from '../../../components/workout/WorkoutAiPromptSheet';
import { TrashIcon } from '../../../components/icons/TrashIcon';

export interface MobileWorkoutBuilderShellProps {
  initialDraft?: WorkoutBuilderInitialDraft;
  edit?: WorkoutBuilderEditConfig;
  userId?: number;
  onCreated?: () => void;
  workoutTypeId?: number | null;
  onWorkoutTypeIdChange?: (id: number | null) => void;
  ftpValue: number;
  ltHrValue: number;
  maxHrValue: number;
  thresholdSpeedMps: number;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function pickCyclingTypeId(types: { id: number; english_name?: string; name?: string }[]): number | null {
  if (!types.length) return null;
  const match = types.find(t => {
    const label = `${t.english_name ?? ''} ${t.name ?? ''}`.toLowerCase();
    return label.includes('ride') || label.includes('cycl') || label.includes('bike');
  });
  return match?.id ?? types[0].id;
}

function workoutTypeLabel(type: WorkoutType, lang: string): string {
  if (lang.startsWith('fa') || lang.startsWith('ar')) {
    return type.name || type.english_name || String(type.id);
  }
  return type.english_name || type.name || String(type.id);
}

/** Mobile Garmin builder orchestration — mirrors Ai-Tempo WorkoutForm tab 2 + WorkoutStepConfig. */
export function MobileWorkoutBuilderShell({
  edit,
  initialDraft,
  userId,
  onCreated,
  workoutTypeId: externalWorkoutTypeId,
  onWorkoutTypeIdChange,
  ftpValue,
  ltHrValue,
  maxHrValue,
  thresholdSpeedMps,
}: MobileWorkoutBuilderShellProps) {
  const { t, i18n } = useTranslation();
  const isEditMode = !!edit;
  const isLibraryEdit = !!edit?.libraryExerciseId;
  const showDateField = !isLibraryEdit;

  const initialSettings = parseWorkoutBuilderSettings(
    (edit?.structureMeta ?? initialDraft?.structure) as Partial<WorkoutBuilderSettings> | undefined,
  );

  const [workoutName, setWorkoutName] = useState(edit?.initialTitle ?? initialDraft?.title ?? '');
  const [workoutDate, setWorkoutDate] = useState(
    edit?.initialDate?.slice(0, 10) || initialDraft?.date || todayIsoDate(),
  );
  const [dragData, setDragData] = useState<ApiStructBlock[]>(() =>
    edit?.initialDragData?.length
      ? JSON.parse(JSON.stringify(edit.initialDragData))
      : initialDraft?.structure.structure ? JSON.parse(JSON.stringify(initialDraft.structure.structure)) : [],
  );
  const [tempo, setTempo] = useState<ApiStructBlock[]>([]);
  const [workoutSettings, setWorkoutSettings] = useState<WorkoutBuilderSettings>(initialSettings);
  const workoutSettingsRef = useRef(workoutSettings);
  workoutSettingsRef.current = workoutSettings;

  const [builderSetupDone, setBuilderSetupDone] = useState(
    !!(edit?.structureMeta?.primaryIntensityMetric ?? initialDraft?.structure.primaryIntensityMetric),
  );
  const [dragActive, setDragActive] = useState(false);
  const [selectedLoop, setSelectedLoop] = useState<string | number | null>(null);

  const [itemModal, setItemModal] = useState(false);
  const [loopModal, setLoopModal] = useState(false);
  const [stepTypeModal, setStepTypeModal] = useState(false);
  const [durationModal, setDurationModal] = useState(false);
  const [secondaryModal, setSecondaryModal] = useState(false);
  const [intensityTarget, setIntensityTarget] = useState(false);
  const [powerMode, setPowerMode] = useState(1);
  const [itemData, setItemData] = useState({ parentIndex: 0, index: 0 });

  const [zoneSettings, setZoneSettings] = useState<Record<string, unknown> | null>(null);
  const [loadingZones, setLoadingZones] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [internalWorkoutTypeId, setInternalWorkoutTypeId] = useState<number | null>(
    edit?.workoutTypeId ?? initialDraft?.workoutTypeId ?? null,
  );
  const [workoutTypes, setWorkoutTypes] = useState<WorkoutType[]>([]);
  const [builderSettingsToolbar, setBuilderSettingsToolbar] = useState<HTMLSpanElement | null>(null);
  const [aiSheetOpen, setAiSheetOpen] = useState(false);

  const workoutTypeId = externalWorkoutTypeId ?? internalWorkoutTypeId;
  const FtpArray = useMemo(() => buildFtpPercentOptions(ftpValue), [ftpValue]);
  const LthrArray = useMemo(() => buildLthrPercentOptions(ltHrValue), [ltHrValue]);

  const setWorkoutTypeId = useCallback(
    (id: number | null) => {
      setInternalWorkoutTypeId(id);
      onWorkoutTypeIdChange?.(id);
    },
    [onWorkoutTypeIdChange],
  );

  useEffect(() => {
    let cancelled = false;
    workouts.getWorkoutTypes()
      .then(res => {
        if (cancelled) return;
        const types = res.data.data?.workoutTypes ?? [];
        setWorkoutTypes(workoutTypesForCreation(types));
        if (edit?.workoutTypeId) {
          setInternalWorkoutTypeId(edit.workoutTypeId);
          onWorkoutTypeIdChange?.(edit.workoutTypeId);
          return;
        }
        if (!isEditMode) {
          const id = initialDraft?.workoutTypeId ?? pickCyclingTypeId(types);
          setInternalWorkoutTypeId(id);
          onWorkoutTypeIdChange?.(id);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [edit?.workoutTypeId, initialDraft?.workoutTypeId, isEditMode, onWorkoutTypeIdChange]);

  const fetchZones = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingZones(true);
      const res = await athleteSettingsApi.getAthleteZones(userId);
      setZoneSettings((res.data?.data?.zones ?? null) as Record<string, unknown> | null);
    } catch {
      setZoneSettings(null);
    } finally {
      setLoadingZones(false);
    }
  }, [userId]);

  const buildStructurePayload = useCallback(
    (blocks: ApiStructBlock[]) => ({
      primaryLengthMetric: workoutSettingsRef.current.primaryLengthMetric,
      primaryIntensityMetric: workoutSettingsRef.current.primaryIntensityMetric,
      primaryIntensityTargetOrRange: workoutSettingsRef.current.primaryIntensityTargetOrRange,
      structure: normalizeWorkoutBlocksTargets(
        blocks,
        workoutSettingsRef.current.primaryIntensityMetric as PrimaryIntensityMetric,
        {
          ftp: ftpValue,
          ltHr: ltHrValue,
          maxHr: maxHrValue,
          thresholdSpeedMps,
        },
        workoutSettingsRef.current.primaryIntensityTargetOrRange,
      ),
    }),
    [ftpValue, ltHrValue, maxHrValue, thresholdSpeedMps],
  );

  const handleSave = useCallback(async () => {
    if (!builderSetupDone) return;
    setSaving(true);
    setSaveError(null);
    try {
      const structure = buildStructurePayload(dragData);
      const metrics = calculatePlannedMetricsFromBlocks(dragData, {
        primaryIntensityMetric: workoutSettings.primaryIntensityMetric,
        primaryIntensityTargetOrRange: workoutSettings.primaryIntensityTargetOrRange,
        ftp: ftpValue,
        ltHr: ltHrValue,
        maxHr: maxHrValue,
        thresholdSpeedMps,
      });

      const payload: Record<string, unknown> = {
        title: workoutName.trim() || t('workoutBuilder.untitled'),
        structure,
        total_time_planned: metrics.total_time_planned > 0 ? metrics.total_time_planned : null,
        tss_planned: metrics.tss_planned != null && metrics.tss_planned > 0 ? metrics.tss_planned : null,
        if_planned: metrics.if_planned != null && metrics.if_planned > 0 ? metrics.if_planned : null,
      };
      if (metrics.distance_planned != null && metrics.distance_planned > 0) {
        payload.distance_planned = metrics.distance_planned;
      }
      if (workoutTypeId) {
        payload.workout_type_id = workoutTypeId;
      }
      if (showDateField && workoutDate) {
        payload.workout_day = `${workoutDate} 00:00:00`;
      }

      if (edit) {
        if (edit.libraryExerciseId) {
          const exercisePayload: Record<string, unknown> = { ...payload };
          delete exercisePayload.workout_day;
          await library.updateExercise(edit.libraryExerciseId, { data: { exercise: exercisePayload } });
        } else if (edit.workoutId) {
          await workouts.updateWorkout(edit.workoutId, payload);
        } else {
          setSaveError(t('workoutBuilder.missingId'));
          return;
        }
        edit.onSaved();
        return;
      }

      if (!userId) {
        setSaveError(t('workoutBuilder.selectAthlete'));
        return;
      }
      if (!workoutTypeId) {
        setSaveError(t('workoutBuilder.typeLoading'));
        return;
      }

      await forms.createWorkout(userId, {
        ...payload,
        workout_type_id: workoutTypeId,
        workout_day: `${workoutDate || todayIsoDate()} 00:00:00`,
      });
      onCreated?.();
    } catch (err: unknown) {
      const apiMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSaveError(apiMsg ?? t('workoutBuilder.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [
    builderSetupDone,
    buildStructurePayload,
    dragData,
    workoutSettings,
    ftpValue,
    ltHrValue,
    maxHrValue,
    thresholdSpeedMps,
    workoutName,
    workoutDate,
    showDateField,
    edit,
    userId,
    workoutTypeId,
    onCreated,
    t,
  ]);

  const handleAiApply = useCallback((payload: WorkoutAiApplyPayload) => {
    const struct = payload.structure;
    const nextSettings = parseWorkoutBuilderSettings(struct as Partial<WorkoutBuilderSettings>);
    workoutSettingsRef.current = nextSettings;
    setWorkoutSettings(nextSettings);
    setBuilderSetupDone(true);
    const blocks = struct.structure ?? [];
    if (blocks.length) {
      setDragData(JSON.parse(JSON.stringify(blocks)));
      setTempo([]);
    }
    if (payload.suggested_title?.trim()) {
      setWorkoutName(payload.suggested_title.trim());
    }
  }, []);

  return (
    <div className="wb-mobile wb-mobile--flutter title-bar-no-drag" style={isEditMode ? { margin: 0, height: '100vh' } : undefined}>
      <div className="wb-mobile__header">
        {isEditMode ? (
          <ActionButton
            type="button"
            className="wb-mobile__header-btn wb-mobile__header-btn--ghost"
            onClick={() => edit?.onCancel()}
            disabled={saving}
          >{t('workoutBuilder.cancel')}</ActionButton>
        ) : (
          <span className="wb-mobile__header-spacer" aria-hidden />
        )}
        <div className="wb-mobile__header-actions">
          {dragData.length > 0 && (
            <ActionIconButton
              type="button"
              className="wb-mobile__header-btn wb-mobile__header-btn--icon"
              onClick={() => { setDragData([]); setTempo([]); }}
              title={t('workoutBuilder.clearStructure')}
              label={t('workoutBuilder.clearStructure')}
              icon={<TrashIcon size={16} />}
            />
          )}
          <span ref={setBuilderSettingsToolbar} className="wf-form__builder-settings" />
          {builderSetupDone && userId != null && (
            <AiActionButton className="wb-mobile__header-btn" onClick={() => setAiSheetOpen(true)}
              title={t('workoutBuilder.ai.buildWithAi')}
            >{t('workoutBuilder.ai.short', { defaultValue: 'AI' })}</AiActionButton>
          )}
          <ActionButton
            type="button"
            className="wb-mobile__header-btn wb-mobile__header-btn--primary"
            variant="primary"
            disabled={saving || !builderSetupDone}
            onClick={() => void handleSave()}
          >{saving ? t('workoutBuilder.saving') : t('workoutBuilder.save')}</ActionButton>
        </div>
      </div>

      <div className="wb-mobile__meta">
        <label className="wb-mobile__meta-field wb-mobile__meta-field--name">
          <span>{t('workoutBuilder.workoutName')}</span>
          <input
            type="text"
            value={workoutName}
            onChange={e => setWorkoutName(e.target.value)}
            placeholder={t('workoutBuilder.workoutName')}
            autoComplete="off"
          />
        </label>
        <div className="wb-mobile__meta-row">
          {showDateField && (
            <label className="wb-mobile__meta-field">
              <span>{t('workoutBuilder.workoutDate')}</span>
              <input
                type="date"
                className="ltr-island"
                value={workoutDate}
                onChange={e => setWorkoutDate(e.target.value)}
              />
            </label>
          )}
          <label className="wb-mobile__meta-field">
            <span>{t('workoutBuilder.sportType')}</span>
            <select
              value={workoutTypeId ?? ''}
              onChange={e => {
                const next = e.target.value ? Number(e.target.value) : null;
                setWorkoutTypeId(Number.isFinite(next) ? next : null);
              }}
            >
              {!workoutTypeId && (
                <option value="">{t('workoutBuilder.selectSport')}</option>
              )}
              {workoutTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {workoutTypeLabel(type, i18n.language)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {saveError && <p className="wb-mobile-error">{saveError}</p>}

      <div className="wb-mobile__scroll">
        <WorkoutBuilder
          settingsToolbar={builderSettingsToolbar}
          data={dragData}
          onItemPress={(index, parentIndex) => {
            setTempo(JSON.parse(JSON.stringify(dragData)));
            if (parentIndex != null) {
              setItemData({ parentIndex, index });
            } else {
              setItemData({ parentIndex: index, index: 0 });
            }
            setItemModal(true);
          }}
          onLoopPress={index => {
            setTempo(JSON.parse(JSON.stringify(dragData)));
            setSelectedLoop(index);
            setLoopModal(true);
          }}
          onDragEnd={(repeat, newData) => {
            if (repeat !== null) {
              const a = [...dragData];
              a[repeat] = { ...a[repeat], steps: newData };
              setDragActive(false);
              setDragData([...a]);
            } else {
              setDragData(newData);
              setDragActive(false);
            }
          }}
          dragActive={dragActive}
          onDragStart={() => setDragActive(true)}
          selectedLoop={selectedLoop}
          loopPress={index => setSelectedLoop(index === selectedLoop ? null : index)}
          Delete={(repeat, _item, index) => {
            if (repeat !== null) {
              const a = [...dragData];
              const b = a[repeat].steps?.filter((_, i) => i !== index);
              a[repeat] = { ...a[repeat], steps: b };
              setDragData(a);
            } else {
              setDragData(dragData.filter((_, i) => i !== index));
            }
          }}
          Duplicate={index => {
            setDragData(prev => duplicateBlockAt(prev, index));
          }}
          onSettingsChange={setWorkoutSettings}
          settingsConfirmed={builderSetupDone}
          onSettingsConfirmed={() => setBuilderSetupDone(true)}
          onSetupCancel={edit?.onCancel}
          initialSettings={builderSetupDone ? workoutSettings : undefined}
          ftpValue={ftpValue}
          ltHrValue={ltHrValue}
        />
      </div>

      <WorkoutStepConfig
        running={workoutTypeId === 1}
        itemModal={itemModal}
        setItemModal={setItemModal}
        loopModal={loopModal}
        setLoopModal={setLoopModal}
        stepTypeModal={stepTypeModal}
        setStepTypeModal={setStepTypeModal}
        durationModal={durationModal}
        setDurationModal={setDurationModal}
        secondaryModal={secondaryModal}
        setSecondaryModal={setSecondaryModal}
        intensityTarget={intensityTarget}
        setIntensityTarget={setIntensityTarget}
        dragData={dragData}
        setDragData={setDragData}
        tempo={tempo}
        setTempo={setTempo}
        itemData={itemData}
        powerMode={powerMode}
        setPowerMode={setPowerMode}
        loadingZones={loadingZones}
        zoneSettings={zoneSettings}
        fetchZones={fetchZones}
        ftpValue={ftpValue}
        FtpArray={FtpArray}
        ltHrValue={ltHrValue}
        LthrArray={LthrArray}
        maxHrValue={maxHrValue}
        thresholdSpeedMps={thresholdSpeedMps}
        selectedLoop={selectedLoop}
        primaryIntensityMetric={workoutSettings.primaryIntensityMetric}
        primaryIntensityTargetOrRange={workoutSettings.primaryIntensityTargetOrRange}
      />

      {userId != null && (
        <WorkoutAiPromptSheet
          open={aiSheetOpen}
          onClose={() => setAiSheetOpen(false)}
          userId={userId}
          workoutTypeId={workoutTypeId}
          workoutSettings={workoutSettings}
          onApply={handleAiApply}
        />
      )}
    </div>
  );
}
